import React, { useMemo, useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, ArrowUpRight, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { imgSrc } from "@/lib/api";
import { formatPrice } from "@/lib/format";

export function searchProperties(list, q) {
  const s = (q || "").trim().toLowerCase();
  if (!s) return [];
  const terms = s.split(/\s+/);
  return list.filter((p) => {
    if (!p.published) return false;
    const hay = [p.project, p.developer, p.location, p.microMarket, p.propertyType, ...(p.category || []), ...(p.configuration || []), ...(p.amenities || []), ...(p.locationHighlights || []), p.description, p.whyStandout].filter(Boolean).join(" ").toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
}

export const PropertySearch = ({ compact = false, onDark = false, placeholder = "Search by project, developer, location or keyword" }) => {
  const { data } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const results = useMemo(() => searchProperties(data.properties, q), [data.properties, q]);
  const show = open && q.trim().length > 0;

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    navigate(`/properties?q=${encodeURIComponent(q.trim())}`);
  };

  const shell = onDark
    ? "border-white/25 bg-white/10 text-white placeholder:text-white/60 backdrop-blur-md focus-within:border-[#9ED3B8]"
    : "border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-primary)] focus-within:border-[var(--accent-gold)]";

  return (
    <div ref={boxRef} className="relative w-full" data-testid="property-search-widget">
      <form onSubmit={submit} className={`flex items-center gap-2 rounded-full border px-4 transition-colors ${compact ? "py-2" : "py-3"} ${shell}`}>
        <Search className={`h-4 w-4 shrink-0 ${onDark ? "text-white/80" : "text-[var(--text-muted)]"}`} />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          aria-label="Search properties"
          data-testid="property-search-input"
          className={`w-full bg-transparent text-sm outline-none ${onDark ? "placeholder:text-white/60" : "placeholder:text-[var(--text-muted)]"}`}
        />
        {q && <button type="button" onClick={() => { setQ(""); setOpen(false); }} aria-label="Clear search" data-testid="property-search-clear" className="opacity-70 hover:opacity-100"><X className="h-4 w-4" /></button>}
        <button type="submit" data-testid="property-search-submit" className="btn-gold hidden !rounded-full !px-4 !py-1.5 text-xs sm:inline-flex">Search</button>
      </form>

      {show && (
        <div className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-2xl" data-testid="property-search-results">
          {results.length === 0 ? (
            <div className="p-6 text-center" data-testid="property-search-empty">
              <p className="font-serif text-lg text-[var(--text-primary)]">No properties match “{q}”.</p>
              <p className="mt-1 text-xs text-[var(--text-muted)]">Try a project, developer or micro-market — or let us curate for you.</p>
              <Link to="/request" onClick={() => setOpen(false)} className="btn-outline-gold mt-4 !py-2 text-xs">Find My Property</Link>
            </div>
          ) : (
            <>
              <ul className="max-h-[60vh] divide-y divide-[var(--border-subtle)] overflow-y-auto">
                {results.slice(0, 6).map((p) => (
                  <li key={p.id}>
                    <Link to={`/properties/${p.id}`} onClick={() => setOpen(false)} data-testid={`search-result-${p.id}`} className="flex items-center gap-4 p-3 transition-colors hover:bg-[var(--accent-a5)]">
                      <img src={imgSrc(p.images?.[0])} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-serif text-lg leading-tight text-[var(--text-primary)]">{p.project}</div>
                        <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-[var(--text-muted)]"><MapPin className="h-3 w-3" /> {p.location} · {p.microMarket} · {p.developer}</div>
                      </div>
                      <div className="hidden text-right sm:block">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">From</div>
                        <div className="font-serif text-[var(--text-primary)]">{formatPrice(p.priceMin)}</div>
                      </div>
                      <ArrowUpRight className="h-4 w-4 shrink-0 text-[var(--accent-gold)]" />
                    </Link>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={submit} data-testid="property-search-viewall" className="flex w-full items-center justify-center gap-1 border-t border-[var(--border-subtle)] py-3 text-xs font-medium text-[var(--accent-gold)] hover:bg-[var(--accent-a5)]">
                View all {results.length} result{results.length === 1 ? "" : "s"} <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertySearch;
